import { QueryError } from '@graphql-box/helpers';

import { useQuery } from '@graphql-box/react';
import { useTheme } from '@mui/material';
import { useEffect } from 'react';
import { ScreenFormatType, SectionType } from '#enums/mediaTypes.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { BackdropImage } from '../BackdropImage/index.tsx';
import { TileCarousel } from '../TileCarousel/index.tsx';
import { VideoButton } from '../VideoButton/index.tsx';
import { formatEpisodes } from './helpers/formatEpisodes.ts';
import { type EpisodesCarouselProps, type SeasonEpisodesData } from './types.ts';

const GET_SEASON_EPISODES = "\nquery GetTvSeasons(\n  $id: ID!\n) {\n  season(id: $id) {\n    ...SeasonEpisodes\n  }\n}\n\nfragment SeasonEpisodes on Season {\n  episodes {\n    episodeNumber\n    name\n    stillPath\n    voteAverage\n    voteCount\n  }\n}\n";

export const EpisodesCarousel = ({ children, id, seasonNumber, setActivePreview, tvId }: EpisodesCarouselProps) => {
  const { config } = useToolbelt();
  const theme = useTheme();

  const { execute, loading, result } = useQuery<SeasonEpisodesData>(GET_SEASON_EPISODES, {
    loading: true,
  });

  useEffect(() => {
    void execute({ variables: { id } }, { data: { initiator: 'EpisodesCarousel' } });
    // We only want to re-execute when id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (result instanceof QueryError) {
    console.error(result);
    return;
  }

  if (result?.errors?.length) {
    console.error(result.errors);
  }

  const items = formatEpisodes(result?.data, tvId, seasonNumber, config);

  if (!loading && items.length === 0) {
    return;
  }

  const tileCarousel = (
    <TileCarousel
      imageType="backdrop"
      items={items}
      renderImage={({ setActiveVideo, src, sx, title, ...rest }) => (
        <BackdropImage
          id={rest.id}
          renderSlot={() => (
            <VideoButton
              setActivePreview={setActivePreview}
              setActiveVideo={setActiveVideo}
              title={title}
              {...rest}
              type={ScreenFormatType.Episode}
            />
          )}
          src={src}
          sx={sx}
          title={title}
          type={SectionType.Episode}
        />
      )}
      setActivePreview={setActivePreview}
      tileContainerSx={{
        [theme.breakpoints.down('sm')]: {
          width: '200px',
        },
        marginRight: theme.spacing(2),
        width: '240px',
      }}
    />
  );

  return children ? children(tileCarousel) : tileCarousel;
};
